<script lang="ts" setup>
import { ref, watch, computed, onMounted } from 'vue';
import { useLocalization } from '@/plugins/localization';
import { FormType } from '@/helpers/Form';
import { TreeNode } from '@/components/ideo/utils';
import { Resource as FileResource } from '@/modules/core/files/services/StorageService';

interface PageModel {
    id: number;
    parentId?: number;
    name: string;
    hasChildren: boolean;
    position: number;
    logo?: FileResource;
    isCategory: boolean;
    categoryName?: string;
    pageId?: number;
    columns?: number;
}

export interface FormSettings {
    settings: {
        model: PageModel[];
    };
}

defineOptions({
    name: 'widget-custom-menu-settings',
});

const { $t } = useLocalization();

const model = defineModel<FormType<FormSettings>>();
const defaults: FormSettings['settings'] = {
    model: [
        {
            id: Date.now(),
            parentId: null,
            name: $t('[[[Nowa kategoria]]]'),
            position: 1,
            logo: null,
            isCategory: true,
            categoryName: $t('[[[Nowa kategoria]]]'),
            pageId: 0,
            hasChildren: false,
            columns: 1,
        },
    ],
};

const columns = [
    { value: 1, text: '1' },
    { value: 2, text: '2' },
    { value: 3, text: '3' },
    { value: 4, text: '4' },
];

const expanded = ref([]);
const selected = ref<number | null>(null);
const editoPageSelectorRef = ref<InstanceType<any>>(null);

const page = computed(() => editoPageSelectorRef.value?.page);
const nodes = computed(() => model.value.settings?.model ?? []);
const form = computed(() => nodes.value.find((p) => p.id == selected.value));
const parentIdOptions = computed(() =>
{
    const map: any = {};
    const result: any[] = [];

    nodes.value.forEach((item) =>
    {
        const disabled = item.id === form.value?.id;

        map[item.id] = {
            key: item.id,
            label: item.name.trim(),
            children: [],
            selectable: !disabled,
            styleClass: { 'p-tree-node-disabled': disabled },
        };
    });

    nodes.value.forEach((item) =>
    {
        if (item.parentId === null)
        {
            result.push(map[item.id]);
        }
        else
        {
            if (map[item.parentId])
            {
                map[item.parentId].children.push(map[item.id]);
            }
        }
    });

    return result;
});

function onAdd(): void
{
    nodes.value.push({ ...defaults.model[0], id: Date.now(), position: nodes.value.length + 1 });
}

function onDelete(node: PageModel): void
{
    const index = nodes.value.findIndex((p) => p.id == node.id);

    if (node.id == selected.value)
    {
        selected.value = null;
    }

    nodes.value.splice(index, 1);

    if (node.hasChildren)
    {
        const children = nodes.value.filter((p) => p.parentId == node.id);

        children.forEach((child) => onDelete(child));
    }
}

const getNodes = () =>
{
    return nodes.value;
};

const getMenu = async (): Promise<any[]> =>
{
    const menu: any[] = [
        {
            name: $t('[[[Usuń]]]'),
            icon: 'icon far fa-trash-alt',
            class: 'text-danger',
            click: onDelete,
            confirm: $t('[[[Potwierdzenie usunięcia]]]'),
            confirmPlacement: 'bottom',
        },
    ];

    return menu;
};

const canSort = (): boolean =>
{
    return true;
};

const onToggle = async (node: TreeNode): Promise<void> =>
{
    if (expanded.value.indexOf(node.id) >= 0)
    {
        expanded.value = expanded.value.filter((p) => p !== node.id);
    }
    else
    {
        expanded.value.push(node.id);
    }
};

const onClick = (node: TreeNode): void =>
{
    selected.value = node?.id;
};

const onSort = async (items: PageModel[]): Promise<void> =>
{
    items.forEach((pos) =>
    {
        const node = nodes.value.find((node) => node.id == pos.id);

        if (node) node.position = pos.position;
    });
};

const active = (node: TreeNode): boolean =>
{
    return node.id == selected.value;
};

const updateParentId = (value: number): void =>
{
    const oldNode = nodes.value.find((p) => p.id == form.value.parentId);

    form.value.parentId = value || null;

    if (oldNode)
    {
        oldNode.hasChildren = nodes.value.some((p) => p.parentId == oldNode.id);
    }

    const newNode = nodes.value.find((p) => p.id == value);

    if (newNode)
    {
        newNode.hasChildren = true;
    }
};

const updateIsCategory = (value: boolean): void =>
{
    form.value.name = value ? form.value.categoryName ?? $t('[[[-]]]') : page.value?.name ?? $t('[[[-]]]');
};

watch(
    () => form.value?.categoryName,
    (value) =>
    {
        if (form.value && form.value.isCategory)
        {
            form.value.name = value || $t('[[[-]]]');
        }
    }
);

watch(
    () => page.value,
    (value) =>
    {
        if (form.value && !form.value.isCategory)
        {
            form.value.name = value?.name?.trim?.() || $t('[[[-]]]');
        }
    },
    { flush: 'post' }
);

onMounted(async () =>
{
    model.value.settings = { ...defaults, ...model.value.settings };

    const min = nodes.value
        .filter((item) => item.parentId == null)
        .reduce((prev, current) => (prev?.position < current.position ? prev : current), null);

    selected.value = min?.id;
});
</script>

<template>
    <div>
        <ideo-button icon="fas fa-plus" class="mb-2" @click="onAdd">
            {{ $t('[[[Dodaj]]]') }}
        </ideo-button>
        <div v-if="getNodes().length == 0">
            <p class="text-center py-3 mb-0">
                {{ $t('[[[Nie ma jeszcze żadnych rekordów]]]') }}
            </p>
        </div>
        <div v-else class="row">
            <div class="col-md-5">
                <ideo-tree
                    :pages="getNodes()"
                    :expanded="expanded"
                    :menu="getMenu"
                    :active="active"
                    :move="canSort"
                    @toggle="onToggle"
                    @sort="onSort"
                    @click="onClick"
                />
            </div>
            <div v-if="selected" class="col-md-7">
                <ideo-form-group :label="$t('[[[Strona/kategoria nadrzędna]]]')">
                    <ideo-form-tree-select
                        name="parentId"
                        :model-value="form.parentId"
                        :options="parentIdOptions"
                        @update:model-value="updateParentId"
                    />
                </ideo-form-group>
                <ideo-form-group :label="$t('[[[Zdjęcie/ikonka]]]')">
                    <filepicker v-model="form.logo" media="images" />
                </ideo-form-group>
                <ideo-form-checkbox v-model="form.isCategory" @update:model-value="updateIsCategory">
                    {{ $t('[[[Kategoria]]]') }}
                </ideo-form-checkbox>
                <ideo-form-group v-if="form.isCategory" :label="$t('[[[Nazwa kategorii]]]')" required>
                    <ideo-form-input v-model="form.categoryName" type="text"></ideo-form-input>
                </ideo-form-group>
                <ideo-form-group v-else :label="$t('[[[Wybierz stronę]]]')">
                    <edito-page-selector
                        v-model="form.pageId"
                        ref="editoPageSelectorRef"
                        :module="null"
                        label=""
                        :website="false"
                        :preview="false"
                        :no-preview="true"
                        class="mb-0"
                    />
                </ideo-form-group>
                <ideo-form-group :label="$t('[[[Ilość kolumn podkategorii]]]')">
                    <ideo-form-select v-model="form.columns" :options="columns" />
                </ideo-form-group>
            </div>
        </div>
    </div>
</template>
